import { Settings } from "lucide-react";
import React from "react";
import type { UserConfiguration } from "../../services/UserConfigurationService";
import type { UserConfigurationSchemaDescriptor } from "../../services/userConfigurationSchemaService";
import UserConfigurationSettingsSummary from "./UserConfigurationSettingsSummary";

interface UserIbkrConfigurationCardProps {
  appearance?: "default" | "alpha";
  isDarkMode: boolean;
  configuration: UserConfiguration | null;
  isConfigurationLoading: boolean;
  schemaDescriptor: UserConfigurationSchemaDescriptor | null;
  isSchemaLoading: boolean;
  onConfigure: () => void;
}

/**
 * The IBKR Configuration widget from the Users page details sidebar, extracted
 * so other admin surfaces can show the same view. Markup is unchanged.
 */
const UserIbkrConfigurationCard: React.FC<UserIbkrConfigurationCardProps> = ({
  appearance = "default",
  isDarkMode,
  configuration,
  isConfigurationLoading,
  schemaDescriptor,
  isSchemaLoading,
  onConfigure,
}) => (
  <div className={appearance === "alpha"? `rounded-v7-panel border border-v7-ink/15 bg-v7-white` : `rounded-lg border ${isDarkMode ?"border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
    <div className={appearance === "alpha" ? "flex items-center justify-between p-3 border-b border-v7-ink/15" : "flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700"}>
      <div className="flex items-center gap-2">
        <Settings className={appearance === "alpha"? `w-4 h-4 text-v7-ink/60` : `w-4 h-4 ${isDarkMode ?"text-gray-400" : "text-gray-500"}`} />
        <h4 className={appearance === "alpha"? `text-sm font-medium text-v7-ink-soft` : `text-sm font-medium ${isDarkMode ?"text-gray-200" : "text-gray-700"}`}>
          IBKR Configuration
        </h4>
      </div>
      <button
        onClick={onConfigure}
        className={appearance === "alpha" ? `text-xs px-2 py-1 rounded-v7-control text-v7-bronze-dark hover:bg-v7-paper` : `text-xs px-2 py-1 rounded ${
          isDarkMode
            ? "text-blue-400 hover:bg-blue-900/30"
            : "text-blue-600 hover:bg-blue-50"
        }`}
      >
        {configuration ? "Edit" : "Configure"}
      </button>
    </div>
    <div className="p-3">
      {isConfigurationLoading ? (
        <div className="flex items-center gap-2">
          <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25" : "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"}></div>
          <span className={appearance === "alpha"? `text-sm text-v7-ink/60` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Loading...</span>
        </div>
      ) : configuration ? (
        <div className="space-y-2">
          {configuration.ibkrAccountId ? (
            <div className="flex items-center justify-between">
              <span className={appearance === "alpha"? `text-xs uppercase tracking-wider text-v7-ink/60` : `text-xs uppercase tracking-wider ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                Account ID
              </span>
              <code className={appearance === "alpha"? `text-sm font-mono text-v7-success` : `text-sm font-mono ${isDarkMode ?"text-green-400" : "text-green-600"}`}>
                {configuration.ibkrAccountId}
              </code>
            </div>
          ) : (
            <div className={appearance === "alpha"? `text-sm italic text-v7-ink/60` : `text-sm italic ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
              No IBKR account configured
            </div>
          )}
          <UserConfigurationSettingsSummary appearance={appearance}
            descriptor={schemaDescriptor}
            data={configuration.data}
            isDarkMode={isDarkMode}
            isLoading={isSchemaLoading}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className={appearance === "alpha"? `text-sm italic text-v7-ink/60` : `text-sm italic ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
            No configuration saved
          </div>
          <UserConfigurationSettingsSummary appearance={appearance}
            descriptor={schemaDescriptor}
            data={null}
            isDarkMode={isDarkMode}
            isLoading={isSchemaLoading}
          />
        </div>
      )}
    </div>
  </div>
);

export default UserIbkrConfigurationCard;
